<script setup>
import { ref, onMounted, onUnmounted, watch, h, computed } from 'vue';
import VueResizable from 'vue-resizable'
import { CloseBold } from '@element-plus/icons-vue'
import Form from '../../components/form.vue' 
import { ElButton, ElNotification } from 'element-plus';
import { useProductStore } from '../../stores/fpe'
import { storeToRefs } from 'pinia'
import VariationPanel from '../field-groups/variation-panel.vue';
import { no } from 'element-plus/es/locale/index.mjs';

const store = useProductStore();
const { openPanel, strings, panelWidth, product, editor_config, selectedVariation, visible, variationPanelWidth, notice } = storeToRefs(store);


const hasAdminBar = document.getElementById('wpadminbar') ? true : false;

const left = computed(() => editor_config.value?.editor_position === 'left' ? 0 : undefined);
const right = computed(() => editor_config.value?.editor_position === 'right' ? 0 : undefined);
const handlers = computed(() => editor_config.value?.editor_position === 'right' ? ['l'] : ['r']);
const selectedVariationDetails = ref(null);
const variationVisible = ref(false);
// adminBarHeight ref
const adminBarHeight = ref(hasAdminBar ? document.getElementById('wpadminbar').offsetHeight : 0);
const panelHeight = ref(window.innerHeight - adminBarHeight.value);

// updted panel height on window resize
window.addEventListener('resize', function(){
    adminBarHeight.value = hasAdminBar ? document.getElementById('wpadminbar').offsetHeight : 0;
    panelHeight.value = window.innerHeight - adminBarHeight.value;
})

onMounted(() => {    
    const fpe_editors = document.querySelectorAll('.fpe-editor');
    fpe_editors.forEach(function(editor){
        const textarea = editor.querySelector('textarea');
        const editor_id = textarea.getAttribute('id');
        
        wp.editor.remove(editor_id);
        wp.editor.initialize(editor_id, {
            tinymce: {
                wpautop: true, 
                plugins : 'charmap colorpicker hr lists paste tabfocus textcolor fullscreen wordpress wpautoresize wpeditimage wpemoji wpgallery wplink wptextpattern',
                toolbar1: 'formatselect,bold,italic,bullist,numlist,blockquote,alignleft,aligncenter,alignright,link,wp_more,spellchecker,fullscreen,wp_adv,listbuttons',
                toolbar2: 'styleselect,strikethrough,hr,forecolor,pastetext,removeformat,charmap,outdent,indent,undo,redo,wp_help',
                textarea_rows : 20,
                init_instance_callback: function (editor) {
                  editor.on('Change', function (e) {
                    
                    if(e.target.id == 'fpe_description'){
                        product.value.description = editor.getContent();
                    }
                    
                    if(e.target.id == 'fpe_short_description'){
                        product.value.short_description = editor.getContent();
                    }
                  });
                }
            },
            quicktags: true,
            mediaButtons: true
        });
        
        // Trigger Add Media 
        const add_media = editor.querySelector('.insert-media');
        add_media.addEventListener('click', function(event){
            
            var elem = jQuery( event.currentTarget ),
                editor = elem.data('editor'),
                options = {
                  frame:    'post',
                  state:    'insert',
                  title:    wp.media.view.l10n.addMedia,
                  multiple: true
                };

              if ( elem.hasClass( 'gallery' ) ) {
                options.state = 'gallery';
                options.title = wp.media.view.l10n.createGalleryTitle;
              }

              wp.media.editor.open( editor, options );
        });

        // Toggle editor mode (Visual/Text)
        const editor_switchers = editor.querySelectorAll('.wp-switch-editor');
        editor_switchers.forEach(function(switcher){
            switcher.addEventListener('click', function(event){
                event.preventDefault();
                window.switchEditors.go(editor_id);
            });
        });
        
    });      
    document.body.classList.add('fpe-panel-open');
})


watch(selectedVariation, async (newSelectedVariation) => {
    if(newSelectedVariation !== null){
        // For new variations, store already has empty defaults populated
        if(store.isNewVariation) {
            selectedVariationDetails.value = store.selectedVariationDetails;
            variationVisible.value = true;
            return;
        }
        try {
            const details = await store.getVariationDetails(newSelectedVariation);
            selectedVariationDetails.value = details;
            variationVisible.value = true;
        } catch (error) {
            console.log('Error fetching variation details', error);
        } finally {
            // loading.value = false;
        }
    } else {
        variationVisible.value = false;
    }
});

const closeVariationPanel = () => {
    variationVisible.value = false;
    selectedVariation.value = null;
    selectedVariationDetails.value = null;
    store.isNewVariation = false;
    store.selectedVariationDetails = null;
    
}

watch(notice, (newNotice) => {
    ElNotification({
        offset: 30,
        dangerouslyUseHTMLString: true,
        message: `<strong>${newNotice.message}</strong>`,
        type: newNotice.isError ? 'error' : 'success',
        customClass : 'fpe-items-center',
        zIndex: 99999,
        duration : 2000,
        position: 'top-right',
    })
}, { deep: true })



</script>

<template>
    <div>  
         
    <vue-resizable 
        :active="handlers" 
        class="fpe-panel" 
        @resize:end="store.updatePanelSize"
        :height="panelHeight" :top="adminBarHeight" :width="panelWidth" :left="left" :right="right" style="position: fixed; z-index: 2000;cursor: unset;">
        <div class="resizable-content">
            <header class="fpe-panel-header">
                <span class="header-title">{{ product.name }}</span>
                <el-icon @click="store.togglePanel"><CloseBold /></el-icon>
            </header> 
            <div class="panel-body">    
                <Form />
            </div>
            <div class="panel-footer">
                <PanelFooter />
            </div>              
        </div>
        <!-- Overlay blocks main panel interaction while variation panel is open -->
        <div
          class="fpe-panel-content-overlay"
          v-if="variationVisible && selectedVariation != null && store.selectedVariationDetails != null"
        ></div>
    </vue-resizable>
    <div class="fpe-panel-overlay"></div>
    <vue-resizable 
        :active="handlers"
        :visible="variationVisible" 
        @close="closeVariationPanel"
        @resize:end="store.updateVariationPanelSize"         
        class="fpe-variation-panel" 
        :height="panelHeight" 
        :top="adminBarHeight" 
        :width="variationPanelWidth" 
        :left="editor_config.editor_position == 'right' ? 'unset' : `calc(0% + ${panelWidth}px)`"
        :style="{ position: 'fixed', zIndex: 2001, cursor: 'unset', right: editor_config.editor_position == 'right' ? `${panelWidth}px` : 'unset' }"
        v-if="variationVisible && selectedVariation != null && store.selectedVariationDetails != null"        
    >
        <variation-panel 
        :variationID="selectedVariation" 
        :variationData="selectedVariationDetails" 
        :visible="variationVisible" 
        @close="closeVariationPanel" />
    </vue-resizable>
</div>
</template>

<style>
    .resizable-content{
        height: 100%;
        display: flex;
        flex-direction: column;
    }
    .resizable-body{
        overflow-y: auto;
        flex:1;
    }
</style>